'use client';

import * as Sentry from '@sentry/nextjs';

import {
  AnalyticsEventName,
  EventWithPayload,
  pushAnalytics,
} from '@/api/analytics';
import { uuid } from '@/helpers/common';
import { timestampByUTC } from '@/helpers/dateTime';
import { STORAGE, useLocalStorageStore } from '@/store';

interface AnalyticsStore {
  sessionId: string;
  createdAt: number;
}

export const getSessionId = () => {
  const analyticsStore = useLocalStorageStore
    .getState()
    .get<AnalyticsStore | null>(STORAGE.ANALYTICS);
  const createdAt = new Date().getTime();

  const searchParams = new URLSearchParams(window.location.search);
  const sessionIdParam = searchParams.get('session_id');

  if (!analyticsStore || sessionIdParam) {
    const sessionId =
      sessionIdParam && sessionIdParam !== 'null' ? sessionIdParam : uuid();

    useLocalStorageStore
      .getState()
      .replace(STORAGE.ANALYTICS, { sessionId, createdAt });
    return sessionId;
  }

  return analyticsStore.sessionId;
};

export const sendEventToAnalyticsFn = (
  event: EventWithPayload,
  userId?: number
) => {
  const eventName = (() => {
    switch (event.name) {
      case AnalyticsEventName.MODAL__ADDITIONAL_USER_DATA__STEP__APPEARANCE:
      case AnalyticsEventName.GAME_ALL_PROVIDERS_PROVIDER:
      case AnalyticsEventName.GAME_REAL_GAME:
      case AnalyticsEventName.GAME_DEMO_GAME:
        return Object.entries<string>(event.dynamicParams)
          .map(([key, value]) => ({ key, value }))
          .reduce((acc, { key, value }) => {
            return acc === ''
              ? event.name.replace(`{{${key}}}`, value)
              : acc.replace(`{{${key}}}`, value);
          }, '');
      default:
        return event.name;
    }
  })();

  const sessionId = getSessionId();

  const userIdT = (() => {
    if (event.attributes?.userId == null) return userId;
    return event.attributes.userId;
  })();

  const setExtraDetails = (
    eventName: string,
    sessionId: string,
    userId?: number
  ): EventWithPayload => {
    return {
      ...event,
      name: eventName as any,
      attributes: {
        ...event.attributes,
        sessionId,
        userId,
        email: event.attributes?.email,
        createdAt: timestampByUTC(),
        updatedAt: timestampByUTC(),
      },
    };
  };

  try {
    return pushAnalytics(setExtraDetails(eventName, sessionId, userIdT));
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setTags({
        category: 'analytics',
        pathToFile: 'src/helpers/analytics.ts',
      });
      scope.setExtras({
        event,
        userId,
        eventName,
        sessionId,
        error: error == null ? 'Unknown error' : JSON.stringify(error),
      });

      scope.setLevel('error');

      Sentry.captureMessage(
        `Error in full information request - ${JSON.stringify(error)}`
      );
    });
  }
};
